/**
 * Режими бібліотеки бази знань у лівому меню (прототип v4 kbNavMain: «Бібліотека · Бібліотеки · Дерево знань · Мапа знань · Найпопулярніші ·
 * Нещодавно оновлені»; «Усі категорії →» — пункт групи «Категорії»). Режим — параметр `?mode=` списку в поданні-бібліотеці; ті самі значення
 * читає LibraryInsight рушія аналітики (home · libraries · categories · tree · map · popular · recent). Чисті функції.
 */
import type { IRecordTypeDefinition } from '@platform/core/types';

/** Параметр адреси з режимом бібліотеки. */
export const libraryModeParam = 'mode';

/** Режими-пункти лівого меню під «Бібліотекою» в порядку прототипу. */
export const libraryMenuModes: readonly string[] = ['libraries', 'tree', 'map', 'popular', 'recent'];

/** Усі режими бібліотеки; `home` — головна (без параметра). */
const MODES: readonly string[] = ['home', 'libraries', 'categories', 'tree', 'map', 'popular', 'recent'];

/** Режим із параметра адреси: відомий режим, інакше головна. */
export function libraryModeOf(value: string | null | undefined): string {
  return value && MODES.includes(value) ? value : 'home';
}

/** Власне подання-бібліотека типу запису (insight kind `library` серед власних подань), інакше undefined. */
export function libraryViewOf(rt: IRecordTypeDefinition): string | undefined {
  return (rt.views.custom ?? []).find((v) => rt.views.settings?.[v]?.insight?.kind === 'library');
}

/**
 * Режим пункту меню метаданих, що відкриває подання `view`: для бібліотеки — явний `mode` пункту, інакше «Усі категорії» (головну
 * бібліотеки вже дає власний пункт меню «Бібліотека»); для інших подань — undefined.
 */
export function libraryItemMode(rt: IRecordTypeDefinition, view: string | undefined, mode?: string): string | undefined {
  if (!view || view !== libraryViewOf(rt)) return undefined;
  return mode ? libraryModeOf(mode) : 'categories';
}

/**
 * Чи активний пункт `mode` блоку бібліотеки: відкрито список типу `rtKey` у поданні-бібліотеці `view` (не сторінку запису) у цьому режимі;
 * головна — лише без пункту меню з фільтром чи пресетом (`item` — немає або пункт подання `view:…`). Чиста функція.
 */
export function libraryLinkActive(
  mode: string, rtKey: string, view: string, route: { rt?: string; id?: string; view?: string }, item: string | null, current: string | null,
): boolean {
  if (route.rt !== rtKey || route.id || route.view !== view || libraryModeOf(current) !== mode) return false;
  return mode !== 'home' || !item || item.startsWith('view:');
}

/** Кількість категорій бібліотеки (варіанти поля групування `insight.by`, типово `category`) — лічильник «Усі категорії →». */
export function libraryCategoryCount(rt: IRecordTypeDefinition, view: string): number | undefined {
  const by = rt.views.settings?.[view]?.insight?.by ?? 'category';
  return rt.fields.find((f) => f.key === by)?.options?.length;
}
